import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import { withStyles } from '@material-ui/core/styles';

import FormComponent from '../FormComponent';
import FMPText from '../pureForm/FMPText';
import FMPProductSelect from '../pureForm/FMPProductSelect';
import FMPSectionHeader from '../ui/FMPSectionHeader';
import Transition from './Transition';
import FuelSubContractContainer from '../../containers/inventory/FuelSubContract';
import { allProducts } from '../../libs/products';

const styles = (theme) => ({
	dialogPaper: {
		overflow: 'scroll',
		maxWidth: '100%',
		width: 800,
		maxHeight: 'calc(100% - 20px)',
		margin: 10,
	},
	dialogPadding: {
		padding: 10,
		[theme.breakpoints.up('md')]: {
			padding: '20px 50px 20px 50px',
		},
	},
	submitButton: {
		fontSize: 16,
		borderRadius: '100px',
		padding: '15px 20px',
		textTransform: 'uppercase',
	},
	note: {
		backgroundColor: '#D8F0FF',
		padding: 20,
		fontSize: 16,
		lineHeight: 1.4,
		marginBottom: 10,
		marginTop: 10,
		border: '1px solid #BADEFB',
	},
});

class SaveFuelSubContractDialog extends FormComponent {
	constructor(props) {
		super(props);
		this.state = {
			formFieldError: {},
			isUpdate: false,
			fuelSubContractId: undefined,
			name: '',
			productType: '',
			provisionalPricing: '',
			finalPricing: '',
			finalCalculatedCosts: [],
		};
	}

	componentDidUpdate(prevProps) {
		const { open, fuelSubContract } = this.props;
		if (!prevProps.open && open) {
			if (fuelSubContract) {
				const newState = this.fillForm();
				newState.isUpdate = true;
				this.setState(newState, this.setupFinalCostsArray);
			} else {
				this.setState(
					{ isUpdate: false, fuelSubContractId: undefined, productType: this.props.parentProductType || '' },
					this.setupFinalCostsArray
				);
			}
		}
	}

	fillForm = () => {
		const { fuelSubContract } = this.props;
		if (!fuelSubContract) {
			return {};
		}

		const newState = {};
		[
			'fuelSubContractId',
			'name',
			'productType',
			'provisionalPricing',
			'finalPricing',
			'finalCalculatedCosts',
		].forEach((key) => {
			if (_.isNil(fuelSubContract[key])) {
				return;
			}
			newState[key] = fuelSubContract[key];
		});
		return newState;
	};

	setupFinalCostsArray = () => {
		const { parentStartDate, parentEndDate } = this.props;
		const { finalCalculatedCosts } = this.state;

		if (!parentStartDate || !parentEndDate) {
			return;
		}

		const indexMoment = moment(parentStartDate);
		const endMoment = moment(parentEndDate);
		const newFinalCalculatedCosts = [];
		while (indexMoment.isBefore(endMoment)) {
			const newStartDate = moment(indexMoment).startOf('month').format('YYYY-MM-DD');
			const newEndDate = moment(indexMoment).endOf('month').format('YYYY-MM-DD');

			let cost = '';
			let bulkCost = '';
			const existing = _.find(finalCalculatedCosts, {
				startDate: newStartDate,
				endDate: newEndDate,
			});
			if (existing) {
				cost = existing.cost || '';
				bulkCost = existing.bulkCost || '';
			}

			newFinalCalculatedCosts.push({
				startDate: newStartDate,
				endDate: newEndDate,
				cost,
				bulkCost,
			});
			indexMoment.add(1, 'month');
		}

		this.setState({ finalCalculatedCosts: newFinalCalculatedCosts });
	};

	handleSave = () => {
		const { showErrors, addFuelSubContract, updateFuelSubContract, parentFuelContractId, onClose } =
			this.props;
		const { isUpdate, fuelSubContractId } = this.state;

		const {
			name,
			productType,
			provisionalPricing,
			finalPricing,
			finalCalculatedCosts,
		} = this.state;

		const params = {
			parentFuelContractId,
			name,
			productType,
			provisionalPricing,
			finalPricing,
			finalCalculatedCosts,
		};

		if (isUpdate) {
			params.fuelSubContractId = fuelSubContractId;
			updateFuelSubContract({
				params,
				callback: (err) => {
					if (err) {
						return showErrors(_.isArray(err) ? err : [err]);
					}
					onClose(true);
				},
			});
		} else {
			addFuelSubContract({
				params,
				callback: (err) => {
					if (err) {
						return showErrors(_.isArray(err) ? err : [err]);
					}
					onClose(true);
				},
			});
		}
	};

	handleClose = () => {
		this.setState({
			formFieldError: {},
			isUpdate: false,
			fuelSubContractId: undefined,
			name: '',
			productType: '',
			provisionalPricing: '',
			finalPricing: '',
			finalCalculatedCosts: [],
		});
		this.props.onClose(false);
	};

	renderFinalCosts = () => {
		const { classes } = this.props;
		const { finalCalculatedCosts } = this.state;
		const { parentStartDate, parentEndDate } = this.props;

		if (!parentStartDate || !parentEndDate) {
			return (
				<Grid item xs={12} sm={12}>
					<div className={classes.note}>
						<strong>Note: </strong>The parent contract must have a start and end date to fill out
						actual costs.
					</div>
				</Grid>
			);
		}

		if (_.isEmpty(finalCalculatedCosts)) {
			return (
				<Grid item xs={12} sm={12}>
					Costs are empty. Select a start date / end date on the parent contract, save, then
					refresh.
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12} sm={12}>
					<FMPSectionHeader label={'Final Actual Costs'} />
				</Grid>
				<Grid item xs={12} sm={12}>
					<Grid container spacing={3}>
						{finalCalculatedCosts.map((finalCalculatedCost, i) => {
							const { startDate, endDate } = finalCalculatedCost;
							const label = `Station Cost for ${moment(startDate).format('M/D/YY')} Thru ${moment(endDate).format('M/D/YY')}`;
							const bulkLabel = `Bulk Cost for ${moment(startDate).format('M/D/YY')} Thru ${moment(endDate).format('M/D/YY')}`;
							const path = `finalCalculatedCosts.${i}.cost`;
							const bulkPath = `finalCalculatedCosts.${i}.bulkCost`;
							return (
								<Fragment key={`finalCalculatedCosts.${i}`}>
									<FMPText
										{...this.setForm(path, label, false)}
										key={path}
										gridProps={{ md: 6 }}
										md={6}
									/>
									<FMPText
										{...this.setForm(bulkPath, bulkLabel, false)}
										key={bulkPath}
										gridProps={{ md: 6 }}
										md={6}
									/>
								</Fragment>
							);
						})}
					</Grid>
				</Grid>
			</>
		);
	};

	render() {
		const { classes, open, fullScreen } = this.props;
		const { isUpdate } = this.state;

		const action = isUpdate ? 'Edit Sub Contract' : 'Add Sub Contract';

		return (
			<Dialog
				fullScreen={fullScreen}
				maxWidth="md"
				open={open}
				TransitionComponent={Transition}
				onClose={this.handleClose}
				classes={{ paper: classes.dialogPaper }}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>{action}</DialogTitle>
					<DialogContent>
						<Grid container spacing={2}>
							<FMPText {...this.setForm('name', 'Name', true)} />

							<FMPProductSelect
								{...this.setForm('productType', 'Product', true)}
								productTypes={_.map(allProducts, ({ value }) => value)}
							/>

							<Grid item xs={12} sm={12}>
								<FMPSectionHeader label={'Pricing Description'} />
							</Grid>

							<FMPText
								{...this.setForm('provisionalPricing', 'Provisional Pricing Description', false)}
								multiline={2}
							/>

							<FMPText
								{...this.setForm('finalPricing', 'Final Pricing Description', false)}
								multiline={2}
							/>

							{this.renderFinalCosts()}
						</Grid>
					</DialogContent>
					<DialogActions>
						<Button onClick={this.handleClose}>Cancel</Button>
						<Button
							variant="contained"
							color="primary"
							className={classes.submitButton}
							onClick={this.handleSave}
						>
							{action}
						</Button>
					</DialogActions>
				</div>
			</Dialog>
		);
	}
}

SaveFuelSubContractDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	fullScreen: PropTypes.bool.isRequired,
	parentFuelContractId: PropTypes.number,
	parentStartDate: PropTypes.string,
	parentEndDate: PropTypes.string,
	fuelSubContract: PropTypes.object,
	parentProductType: PropTypes.string,
	addFuelSubContract: PropTypes.func.isRequired,
	updateFuelSubContract: PropTypes.func.isRequired,
	showErrors: PropTypes.func.isRequired,
};

export default _.flow([withStyles(styles), withMobileDialog(), FuelSubContractContainer])(
	SaveFuelSubContractDialog
);
